import { useEffect, useRef, useState, type ReactNode } from 'react'
import { Link, useLocation } from 'react-router-dom'
import type {
  FavoriteWithRoute,
  InboxResponse,
  ProposalPublic,
  RouteListItem,
} from '@xingroute/schema'
import { api } from '../api/client.js'
import { useAuth } from '../auth/AuthContext.js'
import { useLocale } from '../locale/LocaleContext.js'
import { LevelChip } from '../components/LevelChip.js'
import { levelName } from '../components/LevelChip.js'
import { RouteCard } from '../components/RouteCard.js'
import { Button } from '../components/ui/Button.js'
import { EmptyState } from '../components/ui/EmptyState.js'
import { Badge as UiBadge } from '../components/ui/Misc.js'
import { useToast } from '../components/Toast.js'
import { SegmentedControl } from '../components/ui/SegmentedControl.js'
import { PageToolbar } from '../components/ui/PageToolbar.js'
import { Avatar } from '../components/Avatar.js'
import { AvatarCropDialog } from '../components/AvatarCropDialog.js'
import { friendlyError } from '../lib/friendlyError.js'

type MeTab = 'profile' | 'favorites' | 'collab'

type Loadable<T> =
  | { status: 'idle' | 'loading'; data: null; error: null }
  | { status: 'ready'; data: T; error: null }
  | { status: 'unavailable'; data: null; error: null }
  | { status: 'error'; data: null; error: string }

const INITIAL = { status: 'idle', data: null, error: null } as const
const EMPTY_INBOX: InboxResponse = {
  watched_changes: [],
  pending_reviews: [],
  saved_search_hits: [],
  system_notifications: [],
  unread_system_notification_count: 0,
  next_system_notification_cursor: null,
}

export function MePage() {
  const { t, locale } = useLocale()
  const auth = useAuth()
  const { showToast } = useToast()
  const location = useLocation()
  const [activeTab, setActiveTab] = useState<MeTab>('profile')
  const [favorites, setFavorites] = useState<Loadable<FavoriteWithRoute[]>>(INITIAL)
  const [published, setPublished] = useState<Loadable<RouteListItem[]>>(INITIAL)
  const [drafts, setDrafts] = useState<Loadable<RouteListItem[]>>(INITIAL)
  const [pendingReviewRoutes, setPendingReviewRoutes] = useState<Loadable<RouteListItem[]>>(INITIAL)
  const [proposals, setProposals] =
    useState<Loadable<Array<ProposalPublic & { route_title?: string }>>>(INITIAL)
  const [inbox, setInbox] = useState<Loadable<InboxResponse>>(INITIAL)
  const [loadingMoreNotifications, setLoadingMoreNotifications] = useState(false)
  const authRequestIdentity = `${auth.user?.id ?? ''}:${auth.token ?? ''}`
  const authRequestIdentityRef = useRef(authRequestIdentity)
  authRequestIdentityRef.current = authRequestIdentity
  const paginationOperationRef = useRef(0)
  const avatarInputRef = useRef<HTMLInputElement | null>(null)
  const [avatarCropFile, setAvatarCropFile] = useState<File | null>(null)
  const [avatarBusy, setAvatarBusy] = useState(false)
  const [avatarError, setAvatarError] = useState<string | null>(null)

  async function removeAvatar() {
    if (!auth.token || avatarBusy) return
    setAvatarBusy(true)
    setAvatarError(null)
    try {
      const res = await api.deleteAvatar(auth.token)
      auth.applyAuthResponse(res)
    } catch {
      setAvatarError(t('avatar.errors.failed'))
    } finally {
      setAvatarBusy(false)
    }
  }

  function handleRouteDeleted(id: string) {
    setDrafts((prev) => removeRouteFromLoadable(prev, id))
    setPendingReviewRoutes((prev) => removeRouteFromLoadable(prev, id))
  }

  useEffect(() => {
    const tab = new URLSearchParams(location.search).get('tab')
    if (isMeTab(tab)) setActiveTab(tab)
  }, [location.search])

  useEffect(() => {
    paginationOperationRef.current += 1
    setLoadingMoreNotifications(false)
  }, [authRequestIdentity])

  useEffect(() => {
    if (auth.status !== 'authed' || !auth.token || !auth.user) return
    let cancelled = false
    const token = auth.token

    setFavorites({ status: 'loading', data: null, error: null })
    setPublished({ status: 'loading', data: null, error: null })
    setDrafts({ status: 'loading', data: null, error: null })
    setPendingReviewRoutes({ status: 'loading', data: null, error: null })
    setProposals({ status: 'loading', data: null, error: null })
    setInbox({ status: 'loading', data: null, error: null })

    void api.listFavorites(token).then(
      (data) => {
        if (!cancelled) setFavorites({ status: 'ready', data, error: null })
      },
      (err) => {
        if (!cancelled) setFavorites({ status: 'error', data: null, error: friendlyError(err, t) })
      },
    )
    void api.listMyRoutes(token, 'published').then(
      (data) => {
        if (!cancelled) setPublished({ status: 'ready', data, error: null })
      },
      (err) => {
        if (!cancelled) setPublished({ status: 'error', data: null, error: friendlyError(err, t) })
      },
    )
    void api.listMyRoutes(token, 'draft').then(
      (data) => {
        if (!cancelled) setDrafts({ status: 'ready', data, error: null })
      },
      (err) => {
        if (!cancelled) setDrafts({ status: 'error', data: null, error: friendlyError(err, t) })
      },
    )
    void api.listMyRoutes(token, 'pending_review').then(
      (data) => {
        if (!cancelled) setPendingReviewRoutes({ status: 'ready', data, error: null })
      },
      (err) => {
        if (!cancelled)
          setPendingReviewRoutes({ status: 'error', data: null, error: friendlyError(err, t) })
      },
    )
    void api.listMyProposals(token).then(
      (data) => {
        if (!cancelled) setProposals({ status: 'ready', data, error: null })
      },
      (err) => {
        if (!cancelled) setProposals({ status: 'error', data: null, error: friendlyError(err, t) })
      },
    )
    void api.getInbox(token).then(
      (data) => {
        if (!cancelled) setInbox({ status: 'ready', data, error: null })
      },
      (err) => {
        if (!cancelled) setInbox({ status: 'error', data: null, error: friendlyError(err, t) })
      },
    )

    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- t is stable from context
  }, [auth.status, auth.token, auth.user])

  useEffect(() => {
    if (auth.status !== 'authed' || !auth.token || inbox.status !== 'ready') return
    if (activeTab !== 'collab') return
    if (inbox.data.watched_changes.length === 0 && inbox.data.saved_search_hits.length === 0) return
    void (async () => {
      try {
        await api.markInboxSeen(auth.token!, inbox.data)
      } catch (err) {
        showToast({ message: friendlyError(err, t), tone: 'error' })
      }
    })()
  }, [activeTab, auth.status, auth.token, inbox, showToast, t])

  async function markSystemNotificationsSeen(ids: string[]) {
    if (!auth.token || ids.length === 0) return
    const token = auth.token
    const requestIdentity = authRequestIdentity
    for (let offset = 0; offset < ids.length; offset += 50) {
      const batch = ids.slice(offset, offset + 50)
      try {
        await api.markSystemNotificationsSeen(token, batch)
        if (authRequestIdentityRef.current !== requestIdentity) return
        markSystemNotificationsReadLocally(batch)
        window.dispatchEvent(new Event('xr:inbox-changed'))
      } catch (err) {
        if (authRequestIdentityRef.current === requestIdentity) {
          showToast({ message: friendlyError(err, t), tone: 'error' })
        }
        return
      }
    }
  }

  function markSystemNotificationsReadLocally(ids: string[]) {
    const readAt = new Date().toISOString()
    setInbox((previous) => {
      if (previous.status !== 'ready') return previous
      const idSet = new Set(ids)
      const newlyRead = previous.data.system_notifications.filter(
        (item) => idSet.has(item.id) && item.read_at === null,
      ).length
      return {
        status: 'ready',
        error: null,
        data: {
          ...previous.data,
          system_notifications: previous.data.system_notifications.map((item) =>
            idSet.has(item.id) ? { ...item, read_at: item.read_at ?? readAt } : item,
          ),
          unread_system_notification_count: Math.max(
            0,
            previous.data.unread_system_notification_count - newlyRead,
          ),
        },
      }
    })
  }

  async function loadMoreSystemNotifications() {
    if (
      !auth.token ||
      inbox.status !== 'ready' ||
      !inbox.data.next_system_notification_cursor ||
      loadingMoreNotifications
    ) {
      return
    }
    const requestIdentity = authRequestIdentity
    const operation = paginationOperationRef.current + 1
    paginationOperationRef.current = operation
    setLoadingMoreNotifications(true)
    try {
      const page = await api.getInbox(auth.token, {
        notificationCursor: inbox.data.next_system_notification_cursor,
      })
      if (
        authRequestIdentityRef.current !== requestIdentity ||
        paginationOperationRef.current !== operation
      )
        return
      setInbox((previous) => {
        if (previous.status !== 'ready') return previous
        const existingIds = new Set(previous.data.system_notifications.map((item) => item.id))
        return {
          status: 'ready',
          error: null,
          data: {
            ...previous.data,
            system_notifications: [
              ...previous.data.system_notifications,
              ...page.system_notifications.filter((item) => !existingIds.has(item.id)),
            ],
            unread_system_notification_count: page.unread_system_notification_count,
            next_system_notification_cursor: page.next_system_notification_cursor,
          },
        }
      })
    } catch (err) {
      if (
        authRequestIdentityRef.current === requestIdentity &&
        paginationOperationRef.current === operation
      ) {
        showToast({ message: friendlyError(err, t), tone: 'error' })
      }
    } finally {
      if (paginationOperationRef.current === operation) {
        setLoadingMoreNotifications(false)
      }
    }
  }

  if (auth.status !== 'authed') {
    return (
      <EmptyState
        variant="signin"
        title={t('accountMine')}
        description={t('signInToStart')}
        action={
          <Link to="/signin">
            <Button>{t('signIn')}</Button>
          </Link>
        }
        className="mx-auto max-w-md p-8"
      />
    )
  }

  const display = auth.user?.display_name || auth.user?.username || '—'
  const avatarSeed = auth.user?.username || display

  return (
    <div className="flex flex-col lg:h-full lg:min-h-0 lg:overflow-hidden">
      <PageToolbar>
        <div className="flex min-w-0 items-center gap-2 sm:gap-3">
          <SegmentedControl<MeTab>
            value={activeTab}
            onChange={setActiveTab}
            ariaLabel={t('meTabsAria')}
            className="min-w-0 flex-1 overflow-x-auto [scrollbar-width:none]"
            options={[
              { value: 'profile', label: tabLabel(t('meTabProfile'), null) },
              { value: 'favorites', label: tabLabel(t('meTabFavorites'), countOf(favorites)) },
              { value: 'collab', label: tabLabel(t('meTabCollab'), null) },
            ]}
          />
        </div>
      </PageToolbar>

      <main className="xr-page-container px-5 py-5 pb-24 lg:min-h-0 lg:flex-1 lg:overflow-y-auto">
        {activeTab === 'profile' && (
          <section className="flex max-w-2xl flex-col gap-6" data-testid="me-profile">
            <div className="flex items-start gap-4">
              <div className="flex flex-none flex-col items-center gap-1">
                <button
                  type="button"
                  onClick={() => avatarInputRef.current?.click()}
                  className="rounded-full transition hover:brightness-105"
                  aria-label={t('avatar.change')}
                  data-testid="me-avatar-change"
                >
                  <Avatar
                    avatarUrl={auth.user?.avatar_url}
                    seed={avatarSeed}
                    displayName={display}
                    sizeClass="h-20 w-20 text-2xl"
                    locale={locale === 'zh' ? 'zh' : 'en'}
                  />
                </button>
                {auth.user?.avatar_url && (
                  <button
                    type="button"
                    onClick={() => void removeAvatar()}
                    disabled={avatarBusy}
                    className="text-xs text-[var(--xr-text-secondary)] hover:underline disabled:opacity-50"
                    data-testid="me-avatar-remove"
                  >
                    {t('avatar.remove')}
                  </button>
                )}
                {avatarError && (
                  <p className="text-xs text-[var(--xr-danger-fg)]" data-testid="me-avatar-error">
                    {avatarError}
                  </p>
                )}
                <input
                  ref={avatarInputRef}
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  className="hidden"
                  data-testid="me-avatar-input"
                  onChange={(event) => {
                    const picked = event.target.files?.[0]
                    if (picked) setAvatarCropFile(picked)
                    event.target.value = ''
                  }}
                />
              </div>
              <div className="flex min-w-0 flex-col gap-2">
                <div className="flex min-w-0 flex-wrap items-baseline gap-x-2 gap-y-1">
                  <h1 className="truncate text-xl font-semibold text-[var(--xr-text-primary)]">
                    {display}
                  </h1>
                  {auth.user?.role === 'admin' && (
                    <UiBadge tone="brand" data-testid="me-role-admin">
                      {t('meRoleAdmin')}
                    </UiBadge>
                  )}
                </div>
                <div className="flex min-h-[1.5rem] flex-wrap items-center gap-1">
                  <LevelChip level={auth.user?.level ?? 1} />
                </div>
              </div>
            </div>

            <dl className="grid grid-cols-2 gap-3 sm:grid-cols-3" data-testid="me-profile-stats">
              <ProfileStat value={countOf(favorites)} label={t('meSummaryFavorites')} />
              <ProfileStat value={countOf(published)} label={t('meCollabStatsRoutes')} />
              <ProfileStat value={auth.user?.level ?? 1} label={t('meSummaryLevel')} />
            </dl>

            {auth.user?.created_at && (
              <p
                className="text-xs text-[var(--xr-text-muted)]"
                data-testid="me-profile-member-since"
              >
                {t('meMemberSince').replace(
                  '{{date}}',
                  formatJoinDate(auth.user.created_at, locale),
                )}
              </p>
            )}
          </section>
        )}

        {activeTab === 'favorites' && <FavoritesTab state={favorites} />}
        {activeTab === 'collab' && (
          <CollabTab
            published={published}
            drafts={drafts}
            pendingReviewRoutes={pendingReviewRoutes}
            proposals={proposals}
            inbox={inbox}
            onRouteDeleted={handleRouteDeleted}
            onMarkNotificationsSeen={(ids) => void markSystemNotificationsSeen(ids)}
            onLoadMoreNotifications={() => void loadMoreSystemNotifications()}
            loadingMoreNotifications={loadingMoreNotifications}
          />
        )}
      </main>
      {avatarCropFile && auth.token && (
        <AvatarCropDialog
          file={avatarCropFile}
          token={auth.token}
          onUploaded={(res) => {
            auth.applyAuthResponse(res)
            setAvatarCropFile(null)
          }}
          onClose={() => setAvatarCropFile(null)}
        />
      )}
    </div>
  )
}

function ProfileStat({ value, label }: { value: number | null; label: string }) {
  // Stat card used by the Profile tab. Renders "—" while loading so the
  // user can tell empty (0) apart from pending. Lays out title-then-label
  // (big number on top, small caption underneath) like Apple's Health
  // summary cards.
  return (
    <div className="flex flex-col items-center justify-center gap-1 rounded-[var(--xr-radius-lg)] border border-[var(--xr-border-default)] bg-[var(--xr-bg-surface)] px-3 py-4">
      <dt className="order-2 text-[11px] font-medium uppercase tracking-wider text-[var(--xr-text-muted)]">
        {label}
      </dt>
      <dd className="order-1 text-2xl font-semibold tabular-nums text-[var(--xr-text-primary)]">
        {value !== null ? formatTabCount(value) : '—'}
      </dd>
    </div>
  )
}

function formatJoinDate(iso: string, locale: string): string {
  // Locale-aware "Jun 14, 2026" / "2026年6月14日". Falls back to the raw
  // ISO string on parse failure rather than crashing the page.
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return iso
  try {
    return new Intl.DateTimeFormat(locale === 'zh' ? 'zh-CN' : 'en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    }).format(date)
  } catch {
    return iso
  }
}

function FavoritesTab({ state }: { state: Loadable<FavoriteWithRoute[]> }) {
  const { t } = useLocale()
  return (
    <Section state={state} loading={t('meFavoritesLoading')} errorPrefix={t('meFavoritesError')}>
      {(items) =>
        items.length === 0 ? (
          <EmptyState
            title={t('meFavoritesEmptyTitle')}
            description={t('meFavoritesEmptyDescription')}
            action={
              <Link to="/discover">
                <Button>{t('meFavoritesBrowse')}</Button>
              </Link>
            }
          />
        ) : (
          <ul
            className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3"
            data-testid="me-favorites-grid"
          >
            {items.map((favorite) => (
              <RouteCard
                key={favorite.route_id}
                route={{
                  ...favorite.route,
                  status: 'published',
                  hasGeometry: true,
                }}
                showStatusChip
                showFavoriteHeart={false}
              />
            ))}
          </ul>
        )
      }
    </Section>
  )
}

function CollabTab({
  published,
  drafts,
  pendingReviewRoutes,
  proposals,
  inbox,
  onRouteDeleted,
  onMarkNotificationsSeen,
  onLoadMoreNotifications,
  loadingMoreNotifications,
}: {
  published: Loadable<RouteListItem[]>
  drafts: Loadable<RouteListItem[]>
  pendingReviewRoutes: Loadable<RouteListItem[]>
  proposals: Loadable<Array<ProposalPublic & { route_title?: string }>>
  inbox: Loadable<InboxResponse>
  onRouteDeleted: (id: string) => void
  onMarkNotificationsSeen: (ids: string[]) => void
  onLoadMoreNotifications: () => void
  loadingMoreNotifications: boolean
}) {
  const { t } = useLocale()
  return (
    <div className="flex max-w-3xl flex-col gap-6" data-testid="me-collab">
      {/* Stats summary */}
      <section>
        <h2 className="mb-2 text-sm font-semibold uppercase tracking-wide text-[var(--xr-text-secondary)]">
          {t('meCollabStats')}
        </h2>
        <dl className="grid grid-cols-2 gap-3 sm:grid-cols-4" data-testid="me-collab-stats">
          <ProfileStat value={countOf(published)} label={t('meCollabStatsRoutes')} />
          <ProfileStat value={countOf(proposals)} label={t('meCollabStatsRevisions')} />
          <ProfileStat value={countOf(drafts)} label={t('meCollabStatsDrafts')} />
          <ProfileStat
            value={countOf(pendingReviewRoutes)}
            label={t('meCollabStatsPendingReview')}
          />
        </dl>
      </section>

      {/* Inbox (收件箱) — surfaced first so incoming updates are seen immediately. */}
      <CollabSection
        title={t('meCollabInbox')}
        count={inbox.status === 'ready' ? inboxTotal(inbox.data) : null}
        testid="collab-section-inbox"
      >
        <InboxTab
          state={inbox}
          onMarkSeen={onMarkNotificationsSeen}
          onLoadMore={onLoadMoreNotifications}
          loadingMore={loadingMoreNotifications}
        />
      </CollabSection>

      {/* Pending review (待审阅) */}
      <CollabSection
        title={t('meCollabPendingReview')}
        count={countOf(pendingReviewRoutes)}
        testid="collab-section-pending"
      >
        <Section state={pendingReviewRoutes} loading={t('loading')} errorPrefix={t('loadFailed')}>
          {(items) =>
            items.length === 0 ? (
              <p className="text-sm text-[var(--xr-text-muted)]">
                {t('meCollabPendingReviewEmpty')}
              </p>
            ) : (
              <CompactRouteList items={items} onDeleted={onRouteDeleted} />
            )
          }
        </Section>
      </CollabSection>

      {/* Drafts (草稿) */}
      <CollabSection
        title={t('meCollabDrafts')}
        count={countOf(drafts)}
        testid="collab-section-drafts"
      >
        <Section state={drafts} loading={t('loading')} errorPrefix={t('loadFailed')}>
          {(items) =>
            items.length === 0 ? (
              <p className="text-sm text-[var(--xr-text-muted)]">{t('meCollabDraftsEmpty')}</p>
            ) : (
              <CompactRouteList items={items} onDeleted={onRouteDeleted} />
            )
          }
        </Section>
      </CollabSection>

      {/* Revisions (修订) — proposals to others' routes */}
      <CollabSection
        title={t('meCollabRevisions')}
        count={countOf(proposals)}
        testid="collab-section-revisions"
      >
        <Section state={proposals} loading={t('loading')} errorPrefix={t('loadFailed')}>
          {(items) =>
            items.length === 0 ? (
              <p className="text-sm text-[var(--xr-text-muted)]">{t('meCollabRevisionsEmpty')}</p>
            ) : (
              <ul className="space-y-2">
                {items.map((p) => (
                  <li
                    key={p.id}
                    className="flex items-center justify-between gap-2 rounded-[var(--xr-radius-md)] border border-[var(--xr-border-default)] px-3 py-2"
                  >
                    <div className="min-w-0">
                      <Link
                        to={`/routes/${p.route_id}`}
                        className="truncate text-sm font-medium text-[var(--xr-text-primary)] hover:underline"
                      >
                        {p.route_title || shortId(p.route_id)}
                      </Link>
                      <p className="text-xs text-[var(--xr-text-muted)]">
                        {formatDate(p.created_at)}
                      </p>
                    </div>
                    <UiBadge tone={p.status === 'approved' ? 'brand' : undefined}>
                      {t(`proposalStatus.${p.status}`)}
                    </UiBadge>
                  </li>
                ))}
              </ul>
            )
          }
        </Section>
      </CollabSection>

      {/* Published (发布) */}
      <CollabSection
        title={t('meCollabPublished')}
        count={countOf(published)}
        testid="collab-section-published"
      >
        <Section state={published} loading={t('loading')} errorPrefix={t('loadFailed')}>
          {(items) =>
            items.length === 0 ? (
              <p className="text-sm text-[var(--xr-text-muted)]">{t('meCollabPublishedEmpty')}</p>
            ) : (
              <CompactRouteList items={items} />
            )
          }
        </Section>
      </CollabSection>
    </div>
  )
}

function CollabSection({
  title,
  count,
  testid,
  children,
}: {
  title: string
  count: number | null
  testid: string
  children: ReactNode
}) {
  const [open, setOpen] = useState(true)
  const contentId = `${testid}-content`
  return (
    <section data-testid={testid} aria-label={title}>
      <button
        type="button"
        onClick={() => setOpen((prev) => !prev)}
        aria-expanded={open}
        aria-controls={contentId}
        className="mb-2 flex w-full items-center gap-2 text-left"
        data-testid={`${testid}-toggle`}
      >
        <span aria-hidden className="text-[10px] text-[var(--xr-text-muted)] transition-transform">
          {open ? '▾' : '▸'}
        </span>
        <span
          className="text-sm font-semibold uppercase tracking-wide text-[var(--xr-text-secondary)]"
          data-testid="collab-section-title"
        >
          {title}
        </span>
        {count !== null && <UiBadge>{count}</UiBadge>}
      </button>
      {open && <div id={contentId}>{children}</div>}
    </section>
  )
}

function CompactRouteList({
  items,
  onDeleted,
}: {
  items: RouteListItem[]
  onDeleted?: (id: string) => void
}) {
  const { t } = useLocale()
  return (
    <ul className="space-y-2" data-testid="me-collab-route-list">
      {items.map((route) => (
        <li
          key={route.id}
          className="flex items-center justify-between gap-2 rounded-[var(--xr-radius-md)] border border-[var(--xr-border-default)] px-3 py-2"
        >
          <div className="min-w-0 flex-1">
            <Link
              to={route.slug ? `/r/${route.slug}` : `/routes/${route.id}`}
              className="truncate text-sm font-medium text-[var(--xr-text-primary)] hover:underline"
            >
              {route.title}
            </Link>
            <div className="flex items-center gap-2 text-xs text-[var(--xr-text-muted)]">
              <span>
                {route.activities
                  .map((a: string) => t(`enumLabel_activity_${a}` as never))
                  .join(' · ')}
              </span>
              {route.distance_km && <span>{route.distance_km.toFixed(1)} km</span>}
              <span>{formatDate(route.updated_at)}</span>
            </div>
          </div>
          {(route.status === 'draft' || route.status === 'pending_review') && (
            <UiBadge>{t(`enumLabel_routeStatus_${route.status}`)}</UiBadge>
          )}
          {onDeleted && (route.status === 'draft' || route.status === 'pending_review') && (
            <DraftDeleteButton route={route} onDeleted={onDeleted} />
          )}
        </li>
      ))}
    </ul>
  )
}

function DraftDeleteButton({
  route,
  onDeleted,
}: {
  route: RouteListItem
  onDeleted: (id: string) => void
}) {
  const { t } = useLocale()
  const auth = useAuth()
  const { showToast } = useToast()
  const [busy, setBusy] = useState(false)

  async function handleDelete() {
    if (!auth.token || busy) return
    if (!confirm(t('meCollabDeleteDraftConfirm').replace('{{title}}', route.title))) return
    setBusy(true)
    try {
      await api.deleteRoute(route.id, auth.token, route.etag)
      onDeleted(route.id)
    } catch (err) {
      showToast({ message: friendlyError(err, t), tone: 'error', durationMs: 8000 })
    } finally {
      setBusy(false)
    }
  }

  return (
    <button
      type="button"
      onClick={() => void handleDelete()}
      disabled={busy}
      className="flex-none rounded-[var(--xr-radius-md)] px-2 py-1 text-xs font-medium text-[var(--xr-danger)] hover:bg-[var(--xr-danger-soft)] disabled:opacity-50"
      aria-label={t('meCollabDeleteDraftAria').replace('{{title}}', route.title)}
      data-testid="me-collab-route-delete"
    >
      {t('delete')}
    </button>
  )
}

function InboxTab({
  state,
  onMarkSeen,
  onLoadMore,
  loadingMore,
}: {
  state: Loadable<InboxResponse>
  onMarkSeen: (ids: string[]) => void
  onLoadMore: () => void
  loadingMore: boolean
}) {
  const { t } = useLocale()
  return (
    <Section state={state} loading={t('meInboxLoading')} errorPrefix={t('meInboxError')}>
      {(data) => {
        const inboxData = data ?? EMPTY_INBOX
        const total =
          inboxData.watched_changes.length +
          inboxData.pending_reviews.length +
          inboxData.saved_search_hits.length +
          inboxData.system_notifications.length

        if (total === 0) {
          return (
            <EmptyState title={t('meInboxAllEmptyTitle')} description={t('meInboxAllEmptyDesc')} />
          )
        }

        return (
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-3" data-testid="me-inbox">
            {inboxData.system_notifications.length > 0 && (
              <InboxPanel
                title={t('meInboxSystemNotifications')}
                count={inboxData.unread_system_notification_count}
                className="lg:col-span-3"
                action={
                  inboxData.unread_system_notification_count > 0 ? (
                    <button
                      type="button"
                      className="text-xs font-medium text-[var(--xr-brand-primary)] hover:underline"
                      onClick={() =>
                        onMarkSeen(
                          inboxData.system_notifications
                            .filter((item) => item.read_at === null)
                            .map((item) => item.id),
                        )
                      }
                    >
                      {t('meInboxMarkAllRead')}
                    </button>
                  ) : null
                }
              >
                {inboxData.system_notifications.map((item) => {
                  const unread = item.read_at === null
                  const content =
                    item.kind === 'route_published' ? (
                      <>
                        <p className="font-medium text-[var(--xr-text-primary)]">
                          {t('meInboxRoutePublishedTitle').replace('{{title}}', item.route.title)}
                        </p>
                        <p className="mt-1 text-xs text-[var(--xr-text-muted)]">
                          {t('meInboxRoutePublishedMeta').replace(
                            '{{date}}',
                            formatDateTime(item.created_at),
                          )}
                        </p>
                      </>
                    ) : (
                      <>
                        <p className="font-medium text-[var(--xr-text-primary)]">
                          {t('meInboxLevelUpTitle').replace(
                            '{{mountain}}',
                            levelName(item.to_level, t),
                          )}
                        </p>
                        <p className="mt-1 text-xs text-[var(--xr-text-muted)]">
                          {t('meInboxLevelUpMeta')
                            .replace('{{from}}', String(item.from_level))
                            .replace('{{to}}', String(item.to_level))
                            .replace('{{date}}', formatDateTime(item.created_at))}
                        </p>
                      </>
                    )
                  return (
                    <article
                      key={item.id}
                      className={
                        'flex items-start gap-3 rounded-[var(--xr-radius-md)] border p-3 ' +
                        (unread
                          ? 'border-[var(--xr-brand-primary)]/50 bg-[var(--xr-brand-primary-soft)]'
                          : 'border-[var(--xr-border-default)]')
                      }
                      data-testid={`system-notification-${item.kind}`}
                    >
                      {unread && (
                        <span
                          className="mt-1.5 h-2 w-2 flex-none rounded-full bg-[var(--xr-brand-primary)]"
                          aria-label={t('meInboxUnread')}
                        />
                      )}
                      <div className="min-w-0 flex-1">
                        {item.kind === 'route_published' ? (
                          <Link to={`/r/${item.route.slug}`} className="block hover:underline">
                            {content}
                          </Link>
                        ) : (
                          content
                        )}
                      </div>
                      {unread && (
                        <button
                          type="button"
                          className="flex-none text-xs font-medium text-[var(--xr-brand-primary)] hover:underline"
                          onClick={() => onMarkSeen([item.id])}
                        >
                          {t('meInboxMarkRead')}
                        </button>
                      )}
                    </article>
                  )
                })}
                {inboxData.next_system_notification_cursor && (
                  <Button
                    variant="secondary"
                    className="w-full"
                    disabled={loadingMore}
                    onClick={onLoadMore}
                  >
                    {loadingMore ? t('meInboxLoadingMore') : t('meInboxLoadMore')}
                  </Button>
                )}
              </InboxPanel>
            )}
            {inboxData.watched_changes.length > 0 && (
              <InboxPanel
                title={t('meInboxWatchedChanges')}
                count={inboxData.watched_changes.length}
              >
                {inboxData.watched_changes.map((item) => (
                  <Link
                    key={`${item.route.id}:${item.new_etag}`}
                    to={`/r/${item.route.slug}`}
                    className="block rounded-[var(--xr-radius-md)] border border-[var(--xr-border-default)] p-3 transition hover:border-[var(--xr-border-strong)]"
                  >
                    <p className="font-medium text-[var(--xr-text-primary)]">{item.route.title}</p>
                    <p className="mt-1 text-xs text-[var(--xr-text-muted)]">
                      {t('meInboxWatchedChangeMeta').replace(
                        '{{date}}',
                        formatDate(item.changed_at),
                      )}
                    </p>
                  </Link>
                ))}
              </InboxPanel>
            )}
            {inboxData.pending_reviews.length > 0 && (
              <InboxPanel
                title={t('meInboxPendingReviews')}
                count={inboxData.pending_reviews.length}
              >
                {inboxData.pending_reviews.map((item) =>
                  item.kind === 'publish' ? (
                    <Link
                      key={`publish:${item.route.id}`}
                      to={
                        item.route.slug
                          ? `/r/${item.route.slug}?tab=collab`
                          : `/routes/${item.route.id}?tab=collab`
                      }
                      className="block rounded-[var(--xr-radius-md)] border border-[var(--xr-border-default)] p-3 transition hover:border-[var(--xr-border-strong)]"
                    >
                      <p className="font-medium text-[var(--xr-text-primary)]">
                        {item.route.title}
                      </p>
                      <p className="mt-1 text-xs text-[var(--xr-text-muted)]">
                        {t('meInboxPublishMeta')
                          .replace('{{date}}', formatDate(item.requested_at))
                          .replace(
                            '{{author}}',
                            item.author_name?.trim() || shortId(item.author_id),
                          )}
                      </p>
                    </Link>
                  ) : (
                    <Link
                      key={item.proposal_id}
                      to={`/routes/${item.route_id}?tab=collab`}
                      className="block rounded-[var(--xr-radius-md)] border border-[var(--xr-border-default)] p-3 transition hover:border-[var(--xr-border-strong)]"
                    >
                      <p className="font-medium text-[var(--xr-text-primary)]">
                        {t('meInboxProposalTitle').replace('{{id}}', shortId(item.proposal_id))}
                      </p>
                      <p className="mt-1 text-xs text-[var(--xr-text-muted)]">
                        {t('meInboxProposalMeta')
                          .replace('{{date}}', formatDate(item.proposed_at))
                          .replace(
                            '{{author}}',
                            item.author_name?.trim() || shortId(item.author_id),
                          )}
                      </p>
                    </Link>
                  ),
                )}
              </InboxPanel>
            )}
            {inboxData.saved_search_hits.length > 0 && (
              <InboxPanel
                title={t('meInboxSavedSearchHits')}
                count={inboxData.saved_search_hits.length}
              >
                {inboxData.saved_search_hits.map((item) => (
                  <Link
                    key={`${item.search_id}:${item.new_route_id}`}
                    to={`/routes/${item.new_route_id}`}
                    className="block rounded-[var(--xr-radius-md)] border border-[var(--xr-border-default)] p-3 transition hover:border-[var(--xr-border-strong)]"
                  >
                    <p className="font-medium text-[var(--xr-text-primary)]">{item.search_name}</p>
                    <p className="mt-1 text-xs text-[var(--xr-text-muted)]">
                      {t('meInboxSavedSearchMeta')
                        .replace('{{route}}', shortId(item.new_route_id))
                        .replace('{{date}}', formatDate(item.matched_at))}
                    </p>
                  </Link>
                ))}
              </InboxPanel>
            )}
          </div>
        )
      }}
    </Section>
  )
}

function Section<T>({
  state,
  loading,
  errorPrefix,
  children,
}: {
  state: Loadable<T>
  loading: string
  errorPrefix: string
  children: (data: T) => ReactNode
}) {
  if (state.status === 'idle' || state.status === 'loading') {
    return (
      <p className="text-sm text-[var(--xr-text-muted)]" aria-live="polite" aria-busy="true">
        {loading}
      </p>
    )
  }
  if (state.status === 'error') {
    return (
      <div
        role="alert"
        className="rounded-[var(--xr-radius-lg)] border border-[var(--xr-danger)] bg-[var(--xr-danger-soft)] p-4 text-sm text-[var(--xr-danger)]"
      >
        {errorPrefix}: {state.error}
      </div>
    )
  }
  if (state.status === 'unavailable') return null
  if (state.status === 'ready') return <>{children(state.data)}</>
  return null
}

function InboxPanel({
  title,
  count,
  children,
  className = '',
  action,
}: {
  title: string
  count: number
  children: ReactNode
  className?: string
  action?: ReactNode
}) {
  return (
    <section
      className={`rounded-[var(--xr-radius-lg)] border border-[var(--xr-border-default)] bg-[var(--xr-bg-surface)] p-4 shadow-[var(--xr-shadow-sm)] ${className}`}
    >
      <div className="mb-3 flex items-center justify-between gap-2">
        <h2 className="text-sm font-semibold uppercase tracking-wide text-[var(--xr-text-secondary)]">
          {title}
        </h2>
        <UiBadge>{count}</UiBadge>
        {action}
      </div>
      <div className="space-y-2">{children}</div>
    </section>
  )
}

function tabLabel(label: string, count: number | null): ReactNode {
  // Lock the count slot to a width that accommodates the formatted value
  // (capped at `99+` — three glyphs) using tabular-nums so the segmented
  // control does not reflow when each tab's count resolves on its own
  // schedule. Placeholder is a non-breaking space so the slot stays
  // reserved during the initial loading state.
  return (
    <span className="inline-flex items-baseline gap-1.5">
      <span>{label}</span>
      <span
        className="inline-block min-w-[2em] text-right text-[11px] tabular-nums text-[var(--xr-text-muted)]"
        aria-hidden={count === null}
      >
        {count !== null ? formatTabCount(count) : '\u00A0'}
      </span>
    </span>
  )
}

function formatTabCount(count: number): string {
  return count > 99 ? '99+' : String(count)
}

function countOf<T>(state: Loadable<T[]>): number | null {
  return state.status === 'ready' ? state.data.length : null
}

function removeRouteFromLoadable(
  state: Loadable<RouteListItem[]>,
  id: string,
): Loadable<RouteListItem[]> {
  if (state.status !== 'ready') return state
  return { status: 'ready', data: state.data.filter((route) => route.id !== id), error: null }
}

function isMeTab(value: string | null): value is MeTab {
  return value === 'profile' || value === 'favorites' || value === 'collab'
}

function inboxTotal(data: InboxResponse): number {
  return (
    data.watched_changes.length +
    data.pending_reviews.length +
    data.saved_search_hits.length +
    data.unread_system_notification_count
  )
}

function formatDate(value: string): string {
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return value
  return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
}

function formatDateTime(value: string): string {
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return value
  return date.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

function shortId(value: string): string {
  return value.slice(0, 8)
}
